import { FC, RefObject } from 'react';
import styled from 'styled-components';
import {
  Popover,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Close,
  Apple,
  Google,
  MailOutline,
  Mail,
} from '@mui/icons-material';
import { CalendarType, openCalendarUrl } from '@vs-online-only-app/ui';

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.spacing(3)};
    width: auto;
    min-width: 200px;
  }
`;

const StyledList = styled(List)`
  padding: ${({ theme }) => `${theme.spacing(3)} 0 0 0`};
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(1.5)};
  z-index: 1;
`;

const StyledListItemButton = styled(ListItemButton)`
  .MuiListItemText-primary {
    display: inline-block;
  }

  .MuiListItemText-secondary {
    display: inline-block;
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-left: 4px;
  }
`;

type Props = {
  anchorRef: RefObject<HTMLButtonElement>;
  open: boolean;
  onClose: () => void;
  appleUrl: string;
  googleUrl: string;
  outlookUrl: string;
  outlookWebUrl: string;
  onItemDownloaded: ({ calendarType }: { calendarType: CalendarType }) => void;
};

const CalendarInviteModal: FC<Props> = ({
  anchorRef,
  open,
  onClose,
  appleUrl,
  googleUrl,
  outlookUrl,
  outlookWebUrl,
  onItemDownloaded,
}) => {
  const handleAppleClick = () => {
    openCalendarUrl(appleUrl);
    onItemDownloaded({ calendarType: CalendarType.Apple });
    onClose();
  };

  const handleGoogleClick = () => {
    openCalendarUrl(googleUrl);
    onItemDownloaded({ calendarType: CalendarType.Google });
    onClose();
  };

  const handleOutlookClick = () => {
    openCalendarUrl(outlookUrl);
    onItemDownloaded({ calendarType: CalendarType.Outlook });
    onClose();
  };

  const handleOutlookWebClick = () => {
    openCalendarUrl(outlookWebUrl);
    onItemDownloaded({ calendarType: CalendarType.OutlookWeb });
    onClose();
  };

  return (
    <StyledPopover
      anchorEl={anchorRef?.current}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <CloseButton onClick={onClose}>
        <Close />
      </CloseButton>
      <StyledList>
        {appleUrl && (
          <StyledListItemButton onClick={handleAppleClick}>
            <ListItemIcon>
              <Apple />
            </ListItemIcon>
            <ListItemText primary="Apple" />
          </StyledListItemButton>
        )}
        {googleUrl && (
          <StyledListItemButton onClick={handleGoogleClick}>
            <ListItemIcon>
              <Google />
            </ListItemIcon>
            <ListItemText
              primary="Google"
              secondary="(online)"
            />
          </StyledListItemButton>
        )}
        {outlookUrl && (
          <StyledListItemButton onClick={handleOutlookClick}>
            <ListItemIcon>
              <MailOutline />
            </ListItemIcon>
            <ListItemText primary="Outlook" />
          </StyledListItemButton>
        )}
        {outlookWebUrl && (
          <StyledListItemButton onClick={handleOutlookWebClick}>
            <ListItemIcon>
              <Mail />
            </ListItemIcon>
            <ListItemText
              primary="Outlook.com"
              secondary="(online)"
            />
          </StyledListItemButton>
        )}
      </StyledList>
    </StyledPopover>
  );
};

export default CalendarInviteModal;
